// src/pages/Tickets.js

import React, { useState, useMemo, useContext, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { FiX, FiPlus, FiUsers, FiSettings, FiMaximize, FiMinimize, FiSave, FiTrash2, FiEdit, FiEdit2, FiLayers, FiList, FiFilter, FiClock, FiSearch, FiChevronRight, FiChevronDown } from 'react-icons/fi';

// Core & Reusable Components
import ListView from '../components/ListView';
import CheckboxGroup from '../components/CheckboxGroup';
import AssignTechnicianModal from '../components/AssignTechnicianModal';
import AssignContactsModal from '../components/AssignContactsModal';
import NewListModal from '../components/NewListModal'; 
import Modal from '../components/Modal'; 
import KanbanBoard from '../components/KanbanBoard';
import { useIsMobile } from '../hooks/useIsMobile';

// Context & Auth
import { AuthContext } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';

// CSS for Grid Layout
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const ResponsiveGridLayout = WidthProvider(Responsive);

// ===================================================================
// --- CONSTANTS AND HELPERS ---
// ===================================================================

const ALL_TICKET_COLUMNS = [
    { header: 'Subject', accessor: 'subject' }, 
    { header: 'Client', accessor: 'client' }, 
    { header: 'Priority', accessor: 'priority', render: (t) => <span className={`tag priority-${t.priority.toLowerCase()}`}>{t.priority}</span> }, 
    { header: 'Status', accessor: 'status' }, 
    { header: 'Assigned To', accessor: 'assignedTo' }, 
    { header: 'Created', accessor: 'createdAt', render: (t) => new Date(t.createdAt).toLocaleDateString() },
];
const ALL_STATUSES = ['Open', 'In Progress', 'Pending', 'Waiting On Client', 'Waiting On Parts', 'Closed'];
const ALL_PRIORITIES = ['Critical', 'High', 'Medium', 'Low'];

const applyTicketFilters = (ticket, filters) => {
    const { status = [], priority = [], departments = [] } = filters;
    if (status.length > 0 && !status.includes(ticket.status)) return false;
    if (priority.length > 0 && !priority.includes(ticket.priority)) return false;
    if (departments.length > 0 && !departments.some(dept => (ticket.departments || []).includes(dept))) return false;
    return true;
};

const checkSubTicketActivity = (parentTicketId, allTickets) => {
    return allTickets.some(t => {
        const pId = typeof t.parentId === 'object' ? t.parentId?._id : t.parentId;
        return pId === parentTicketId && t.activeCheckIns && t.activeCheckIns.length > 0;
    });
};

const formatDuration = (totalSeconds) => {
    if (totalSeconds < 0) totalSeconds = 0;
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const TimeTrackingBadge = ({ startTime }) => {
    const [elapsedSeconds, setElapsedSeconds] = useState(() => (Date.now() - new Date(startTime).getTime()) / 1000);

    useEffect(() => {
        const interval = setInterval(() => {
            setElapsedSeconds((Date.now() - new Date(startTime).getTime()) / 1000);
        }, 1000);
      
        return () => clearInterval(interval);
    }, [startTime]);

    return (
        <div className="time-tracking-badge" title="Time Elapsed Since Check-In">
            <FiClock size={12} />
            <span>{formatDuration(elapsedSeconds)}</span>
        </div>
    );
};

const isTicketOverdue = (ticket, appointments) => {
    if (!ticket || ticket.status === 'Closed') { return false; } 
    const now = new Date(); 
    const twoDaysInMs = 2 * 24 * 60 * 60 * 1000;
    const futureAppointments = (appointments || []).filter(app => { 
        const ticketId = typeof app.ticketId === 'object' ? app.ticketId._id : app.ticketId; 
        return ticketId === ticket._id && new Date(app.startTime) > now; 
    }).sort((a, b) => new Date(a.startTime) - new Date(b.startTime)); 
    
    const soonestFutureAppointment = futureAppointments[0]; 
    if (!soonestFutureAppointment) { 
        const createdAt = new Date(ticket.createdAt); 
        if (now.getTime() - createdAt.getTime() > twoDaysInMs) { return true; } 
    } 
    
    const allAppointmentsForTicket = (appointments || []).filter(app => { 
        const ticketId = typeof app.ticketId === 'object' ? app.ticketId._id : app.ticketId; 
        return ticketId === ticket._id; 
    }).sort((a, b) => new Date(b.startTime) - new Date(a.startTime)); 
    
    if (allAppointmentsForTicket.length > 0) { 
        const latestAppointmentTime = new Date(allAppointmentsForTicket[0].startTime); 
        const lastUpdatedTime = new Date(ticket.updatedAt || ticket.createdAt); 
        if (now.getTime() - latestAppointmentTime.getTime() > twoDaysInMs && lastUpdatedTime.getTime() < latestAppointmentTime.getTime()) { return true; } 
    } 
    return false;
};

// ===================================================================
// --- MODALS ---
// ===================================================================

const AddTicketModal = ({ isOpen, onClose, onCreateTicket, communities, departments, technicians, contacts, onAddContact }) => {
    const initialState = { subject: '', client: '', departments: [], priority: 'Medium', body: '', assignedTechnicianIds: [], contactIds: [] };
    const [ticketData, setTicketData] = useState(initialState);
    const [isAssignTechModalOpen, setIsAssignTechModalOpen] = useState(false);
    const [isAssignContactModalOpen, setIsAssignContactModalOpen] = useState(false);
    const [communitySearch, setCommunitySearch] = useState('');
    const [isCommunityDropdownOpen, setIsCommunityDropdownOpen] = useState(false);
    const communityDropdownRef = useRef(null);
    const { showAlert } = useModal(); 
    
    const handleMainClose = () => {
        setIsAssignTechModalOpen(false);
        setIsAssignContactModalOpen(false);
        setIsCommunityDropdownOpen(false);
        onClose();
    };

    useEffect(() => { 
        if (isOpen) { 
            setTicketData(initialState);
            setCommunitySearch('');
        } 
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (communityDropdownRef.current && !communityDropdownRef.current.contains(event.target)) {
                setIsCommunityDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const availableContacts = useMemo(() => {
        if (!ticketData.client || !contacts || !communities) return [];
        const selectedCommunity = communities.find(c => c.name === ticketData.client);
        if (!selectedCommunity) return [];
        return contacts.filter(contact => contact.communityId === selectedCommunity._id);
    }, [ticketData.client, communities, contacts]);

    const filteredCommunities = useMemo(() => {
        if (!communitySearch) return communities;
        return communities.filter(c => c.name.toLowerCase().includes(communitySearch.toLowerCase()));
    }, [communities, communitySearch]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'client') {
            setTicketData(prev => ({ ...prev, [name]: value, contactIds: [] }));
        } else {
            setTicketData(prev => ({ ...prev, [name]: value }));
        }
    };
    const handleDepartmentsChange = (selectedDepts) => { setTicketData(prev => ({ ...prev, departments: selectedDepts })); };
    const handleSaveTechnicians = (technicianIds) => {
        setTicketData(prev => ({ ...prev, assignedTechnicianIds: technicianIds }));
        setIsAssignTechModalOpen(false);
    };
    const handleSaveContacts = (contactIds) => {
        setTicketData(prev => ({ ...prev, contactIds: contactIds }));
        setIsAssignContactModalOpen(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!ticketData.subject || !ticketData.client) { 
             showAlert('Please provide a subject and select a client.', 'Validation Error');
            return; 
        }
        await onCreateTicket(ticketData);
        handleMainClose();
    };

    const handleCommunitySelect = (communityName) => {
        setTicketData(prev => ({ ...prev, client: communityName, contactIds: [] }));
        setCommunitySearch(communityName);
        setIsCommunityDropdownOpen(false);
    };

    const handleCreateContact = async (contactData) => {
        if (!ticketData.client) {
            showAlert('Cannot add contact: Please select a Client (Community) first.', 'Error');
            return;
        }
        const community = communities.find(c => c.name === ticketData.client);
        if (!community) {
            showAlert('Invalid community selected.', 'Error');
            return;
        }
        const result = await onAddContact({ ...contactData, communityId: community._id });
        if (result && result.success && result.data && result.data._id) {
             setTicketData(prev => ({ ...prev, contactIds: [...prev.contactIds, result.data._id] }));
        }
    };

    if (!isOpen) return null;

    const assignedTechniciansText = ticketData.assignedTechnicianIds.length > 0 ? `${ticketData.assignedTechnicianIds.length} technician(s) assigned` : 'Assign Technicians';
    const assignedContactsText = ticketData.contactIds.length > 0 ? `${ticketData.contactIds.length} contact(s) assigned` : 'Assign Contacts';
    
    const isModalVisible = !isAssignTechModalOpen && !isAssignContactModalOpen;

    const footerButtons = (
        <>
            <button type="button" className="btn btn-secondary" onClick={handleMainClose}>Cancel</button>
            <button type="submit" form="create-ticket-form" className="btn btn-primary">Create Ticket</button>
        </>
    );

    return (
        <>
            <AssignTechnicianModal isOpen={isAssignTechModalOpen} onClose={() => setIsAssignTechModalOpen(false)} technicians={technicians} onSave={handleSaveTechnicians} currentTechnicianIds={ticketData.assignedTechnicianIds}/>
            
            <AssignContactsModal 
                isOpen={isAssignContactModalOpen} 
                onClose={() => setIsAssignContactModalOpen(false)} 
                contacts={availableContacts} 
                onSave={handleSaveContacts} 
                currentContactIds={ticketData.contactIds} 
                onCreate={handleCreateContact} 
            />

            <Modal
                isOpen={isOpen && isModalVisible}
                onClose={handleMainClose}
                title="Create New Ticket"
                footer={footerButtons}
                contentStyle={{ height: 'calc(100vh - 8rem)', maxHeight: '800px', display: 'flex', flexDirection: 'column' }}
            >
                <form id="create-ticket-form" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                    <div className="form-group"><label htmlFor="subject">Subject</label><input type="text" name="subject" value={ticketData.subject} onChange={handleChange} required className="form-input" autoFocus/></div>
                    <div className="form-group searchable-select-container" ref={communityDropdownRef}>
                        <label htmlFor="client-search">Client (Community)</label>
                        <input
                            id="client-search"
                            type="text"
                            className="form-input"
                            placeholder="Search for a community..."
                            value={ticketData.client || communitySearch}
                            onChange={(e) => {
                                setCommunitySearch(e.target.value);
                                setTicketData(prev => ({ ...prev, client: '', contactIds: [] }));
                                setIsCommunityDropdownOpen(true);
                            }}
                            onFocus={() => setIsCommunityDropdownOpen(true)}
                            required
                            autoComplete="off"
                        />
                        {isCommunityDropdownOpen && (
                            <div className="searchable-select-dropdown">
                                {filteredCommunities.length > 0 ? filteredCommunities.map(c => <div key={c._id} className="searchable-select-item" onClick={() => handleCommunitySelect(c.name)}>{c.name}</div>) : <div className="searchable-select-item-empty">No communities found.</div>}
                            </div>
                        )}
                    </div>
                    <div className="form-group"><label>Point of Contact</label><button type="button" className="btn btn-secondary btn-block" onClick={() => setIsAssignContactModalOpen(true)} disabled={!ticketData.client}><FiUsers className="btn-icon" />{assignedContactsText}</button>{!ticketData.client && <small className="form-group-description" style={{ marginTop: '0.25rem' }}>Please select a client to see available contacts.</small>}</div>
                    <div className="form-group"><label>Technicians</label><button type="button" className="btn btn-secondary btn-block" onClick={() => setIsAssignTechModalOpen(true)}><FiUsers className="btn-icon" />{assignedTechniciansText}</button></div>
                    <div className="form-group"><label htmlFor="priority">Priority</label><select name="priority" value={ticketData.priority} onChange={handleChange} required className="form-select"><option>Low</option><option>Medium</option><option>High</option><option>Critical</option></select></div>
                    <CheckboxGroup title="Departments" options={(departments || []).filter(d => d.name !== 'Administrative' && d.name !== 'Admin').map(d => d.name)} selected={ticketData.departments} onChange={handleDepartmentsChange} />
                    <div className="form-group" style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                        <label htmlFor="body">Description</label>
                        <textarea name="body" value={ticketData.body} onChange={handleChange} className="form-textarea" style={{ flexGrow: 1 }}></textarea>
                    </div>
                </form>
            </Modal>
        </>
    );
};

const ManageGroupsModal = ({ isOpen, onClose, ticketGroups, onAdd, onDelete }) => {
    const [newGroupName, setNewGroupName] = useState('');
    const [editingGroup, setEditingGroup] = useState(null);
    const addInputRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setNewGroupName('');
            setEditingGroup(null);
            setTimeout(() => addInputRef.current?.focus(), 100);
        }
    }, [isOpen]);

    const handleAddSubmit = (e) => {
        e.preventDefault();
        if (!newGroupName.trim()) return;
        onAdd({ name: newGroupName });
        setNewGroupName('');
    };

    const handleUpdateSubmit = (e) => { e.preventDefault(); setEditingGroup(null); };
    const handleCancelEdit = () => setEditingGroup(null);

    return (
        <Modal 
            isOpen={isOpen} 
            onClose={onClose} 
            title="Manage Ticket Groups" 
            size="slim"
            footer={<button type="button" className="btn btn-secondary" onClick={onClose}>Done</button>}
        >
            <div className="items-list" style={{ maxHeight: '250px', marginBottom: '1rem', overflowY: 'auto' }}>
                {(ticketGroups || []).map(group => (
                    <div key={group._id} className="list-item-row">
                        {editingGroup && editingGroup._id === group._id ? (
                            <form onSubmit={handleUpdateSubmit} style={{ display: 'flex', width: '100%', gap: '0.5rem' }}>
                                <input type="text" value={editingGroup.name} onChange={(e) => setEditingGroup(g => ({ ...g, name: e.target.value }))} className="form-input" autoFocus />
                                <button type="submit" className="btn btn-primary btn-sm"><FiSave /></button>
                                <button type="button" className="btn btn-secondary btn-sm" onClick={handleCancelEdit}><FiX/></button>
                            </form>
                        ) : (
                            <><div className="item-info"><span className="item-title">{group.name}</span></div><div className="item-actions"><button onClick={() => onDelete(group._id)} className="btn-icon-only btn-icon-danger"><FiTrash2 size={16} /></button></div></>
                        )}
                    </div>
                ))}
            </div>
            <form onSubmit={handleAddSubmit}>
                <div className="form-group"><label className="form-label">Add New Group</label><div style={{ display: 'flex', gap: '0.5rem' }}><input ref={addInputRef} type="text" name="newGroup" className="form-input" placeholder="e.g., Q1 Projects" value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} /><button type="submit" className="btn btn-primary">Add</button></div></div>
            </form>
        </Modal>
    );
};

const NewSmartListModal = ({ isOpen, onClose, onSave, communities }) => {
    const initialState = { name: '', filters: { communities: [], statuses: [], priorities: [] } };
    const [config, setConfig] = useState(initialState);
    const { showAlert } = useModal(); 
    
    useEffect(() => { if (isOpen) setConfig(initialState); }, [isOpen]);

    const handleFilterChange = (key, value) => {
        setConfig(prev => ({ ...prev, filters: { ...prev.filters, [key]: value } }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!config.name.trim()) { 
            showAlert('Please give the list a name.', 'Validation Error');
            return; 
        }
        onSave({ id: `smartlist-${Date.now()}`, ...config });
        onClose();
    };

    const footerButtons = (
        <>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" form="smart-list-form" className="btn btn-primary">Create List</button>
        </>
    );

    return (
        <Modal 
            isOpen={isOpen} 
            onClose={onClose} 
            title="Add New Smart List" 
            size="wide"
            footer={footerButtons}
        >
            <form id="smart-list-form" onSubmit={handleSubmit}>
                <div className="form-group"><label className="form-label">List Name</label><input type="text" className="form-input" value={config.name} onChange={e => setConfig(prev => ({...prev, name: e.target.value }))} required autoFocus/></div>
                <hr className="section-divider" />
                <h3 className="form-label" style={{ marginBottom: '1rem' }}>Filters (Leave a section blank to include all)</h3>
                <div style={{maxHeight: '150px', overflowY: 'auto', border: '1px solid var(--gray-200)', borderRadius: '8px', padding: '1rem', marginBottom: '1rem'}}>
                    <CheckboxGroup title="Communities" options={communities.map(c => c.name).sort()} selected={config.filters.communities} onChange={val => handleFilterChange('communities', val)} />
                </div>
                <CheckboxGroup title="Statuses" options={ALL_STATUSES} selected={config.filters.statuses} onChange={val => handleFilterChange('statuses', val)} />
                <CheckboxGroup title="Priorities" options={ALL_PRIORITIES} selected={config.filters.priorities} onChange={val => handleFilterChange('priorities', val)} />
            </form>
        </Modal>
    );
};


// ===================================================================
// --- MOBILE-SPECIFIC COMPONENTS ---
// ===================================================================

const MobileTicketCard = ({ ticket, technicians, isOverdue, isSubTicket, hasChildren, isExpanded, onToggleExpansion, isCheckedInByMe, subTicketActive, index = 0 }) => {
    const navigate = useNavigate();
    const { user } = useContext(AuthContext); 
    const isClient = user?.roles?.includes('Client'); 

    const assignedTechnicians = useMemo(() => {
        return technicians
            .filter(tech => ticket.assignedTechnicianIds.includes(tech._id))
            .map(tech => tech.name.split(' ')[0]); 
    }, [technicians, ticket.assignedTechnicianIds]);

    const handleToggle = (e) => {
        e.stopPropagation();
        onToggleExpansion(ticket._id);
    };
    const cardClasses = `mobile-ticket-card ${isCheckedInByMe ? 'checked-in-by-me' : ''} ${isOverdue ? 'overdue' : ''} ${ticket.status === 'Closed' ? 'closed' : ''} ${isSubTicket ? 'sub-ticket' : ''}`;

    return (
        <div 
            className={cardClasses} 
            onClick={() => navigate(`/tickets/${ticket._id}`)}
            style={{ 
                animation: 'slideFadeInWithGlow 0.4s ease-out backwards',
                animationDelay: `${index * 0.05}s`
            }}
        >
            <div className="mobile-ticket-card-header">
                <div className="mobile-ticket-subject-wrapper">
                    {isSubTicket && <span className="sub-ticket-indicator-mobile">└</span>}
                    {hasChildren && (
                        <button className="mobile-ticket-toggle" onClick={handleToggle}>
                            {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
                        </button>
                    )}
                    <span className="mobile-ticket-client">{ticket.client}</span>
                </div>
                {!isClient && (
                    <span className={`tag priority-${(ticket.priority || 'low').toLowerCase()}`}>{ticket.priority}</span>
                )}
            </div>
            <p className="mobile-ticket-subject">
                {isCheckedInByMe && <span className="active-timer-dot" title="You are clocked in"></span>}
                {ticket.subject}
            </p>
            <div className="mobile-ticket-card-footer">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {subTicketActive && ticket.status !== 'In Progress' && !isSubTicket && (
                        <span className="mobile-ticket-status-badge" style={{ backgroundColor: '#f3e8ff', color: '#7e22ce', border: '1px solid #d8b4fe' }}>
                            Sub Active
                        </span>
                    )}
                    <span className="mobile-ticket-status-badge">
                        {isCheckedInByMe ? <strong style={{color: 'var(--green-700)'}}>YOU ARE CHECKED IN</strong> : ticket.status}
                    </span>
                </div>
                <span className="mobile-ticket-techs">
                    {assignedTechnicians.length > 0 ? `Assigned to ${assignedTechnicians.join(', ')}` : 'Unassigned'}
                </span>
            </div>
        </div>
    );
};

const MobileTicketsView = ({ tickets, technicians, appointments, onNewTicketClick, canManageTickets, expandedTickets, toggleTicketExpansion, ticketMapWithChildren, myTechnicianId }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const isOverdueChecker = useCallback((ticket) => isTicketOverdue(ticket, appointments), [appointments]);
    
    const displayableTickets = useMemo(() => {
        let baseTickets = tickets.filter(t => 
            !searchTerm || 
            t.subject.toLowerCase().includes(searchTerm.toLowerCase()) || 
            t.client.toLowerCase().includes(searchTerm.toLowerCase())
        );

        const result = [];
        const topLevelTickets = baseTickets.filter(t => !t.parentId || !ticketMapWithChildren.has(typeof t.parentId === 'object' ? t.parentId._id : t.parentId));

        for (const ticket of topLevelTickets) {
            result.push(ticket);
            if (expandedTickets.has(ticket._id)) {
                const populatedTicket = ticketMapWithChildren.get(ticket._id);
                if (populatedTicket && populatedTicket.children.length > 0) {
                    const sortedChildren = populatedTicket.children.sort((a,b) => new Date(a.createdAt) - new Date(b.createdAt));
                    for (const child of sortedChildren) {
                        result.push({ ...child, isSubTicket: true });
                    }
                }
            }
        }
        return result;
    }, [tickets, searchTerm, expandedTickets, ticketMapWithChildren]);

    return (
        <div className="page-container mobile-tickets-page">
            <div className="mobile-filter-bar">
                <div className="search-input-wrapper">
                    <FiSearch className="search-icon" />
                    <input 
                        type="search" 
                        className="search-input" 
                        placeholder="Search tickets..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div className="mobile-ticket-list">
                {displayableTickets.length > 0 ? (
                    displayableTickets.map((ticket, index) => {
                        const isCheckedInByMe = myTechnicianId && ticket.activeCheckIns?.some(ci => ci.technicianId === myTechnicianId);
                        const subTicketActive = checkSubTicketActivity(ticket._id, tickets);

                        return (
                            <MobileTicketCard 
                                key={ticket._id}
                                ticket={ticket}
                                technicians={technicians}
                                isOverdue={isOverdueChecker(ticket)}
                                isSubTicket={ticket.isSubTicket || false}
                                hasChildren={ticketMapWithChildren.get(ticket._id)?.children.length > 0}
                                isExpanded={expandedTickets.has(ticket._id)}
                                onToggleExpansion={toggleTicketExpansion}
                                isCheckedInByMe={isCheckedInByMe}
                                subTicketActive={subTicketActive}
                                index={index}
                            />
                        )
                    })
                ) : (
                    <div className="no-tasks-message">
                        <p>No tickets found.</p>
                    </div>
                )}
            </div>
            
            {canManageTickets && (
                <button className="fab-add-ticket" onClick={onNewTicketClick} title="Create New Ticket">
                    <FiPlus size={24} />
                </button>
            )}
        </div>
    );
};


// ===================================================================
// --- MAIN TICKETS COMPONENT ---
// ===================================================================

const Tickets = ({ 
    isLoading, tickets, technicians, communities, contacts, departments, 
    onCreateTicket, onDeleteMultipleTickets, appointments, userDashboardSettings, 
    onDashboardChange, ticketGroups, onAddGroup, onDeleteGroup, onUpdateTicket,
    onAddContact 
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useContext(AuthContext);
    const isMobile = useIsMobile();
    const { showConfirm } = useModal();
    
    const isClient = useMemo(() => user?.roles?.includes('Client'), [user]);

    const [optimisticTickets, setOptimisticTickets] = useState(tickets);
    
    const [isAddTicketModalOpen, setAddTicketModalOpen] = useState(false);
    const [isConfigModalOpen, setConfigModalOpen] = useState(false);
    const [isManageGroupsModalOpen, setIsManageGroupsModalOpen] = useState(false);
    const [isSmartListModalOpen, setIsSmartListModalOpen] = useState(false);
    const [currentBreakpoint, setCurrentBreakpoint] = useState('lg');
    const [fullscreenId, setFullscreenId] = useState(null);
    const [viewMode, setViewMode] = useState('list');
    const [expandedTickets, setExpandedTickets] = useState(new Set());
    
    const [selectedIds, setSelectedIds] = useState([]);

    // ✅ NEW: Track the ID of the list being edited
    const [editingListId, setEditingListId] = useState(null);

    const canManageTickets = useMemo(() => user?.roles?.some(role => ['Giga Admin', 'Admin'].includes(role)), [user]);
    const isOverdueChecker = useCallback((ticket) => isTicketOverdue(ticket, appointments), [appointments]);
    const allDepartments = useMemo(() => (departments || []).map(d => d.name).sort(), [departments]);
    
    const currentUserTechnicianProfile = useMemo(() => {
        if (!user || !technicians) return null;
        return technicians.find(tech => (tech.userId?._id || tech.userId) === user.id);
    }, [user, technicians]);
    
    const listViewFilterGroups = useMemo(() => [
        { title: 'Status', options: ALL_STATUSES, key: 'status' },
        { title: 'Priority', options: ALL_PRIORITIES, key: 'priority' },
        { title: 'Departments', options: allDepartments, key: 'departments' }
    ], [allDepartments]);

    const lists = userDashboardSettings?.lists || [];
    const smartLists = userDashboardSettings?.smartLists || [];
    const layouts = userDashboardSettings?.layouts || {};
    
    useEffect(() => { setOptimisticTickets(tickets); }, [tickets]);
    useEffect(() => {
        if (location.state?.action === 'new' && canManageTickets) {
            setAddTicketModalOpen(true);
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, canManageTickets, navigate]);

    // Handler to save list state (columns, sort, widths)
    const handleListStateChange = useCallback((listId, newState) => {
        const newLists = lists.map(list => {
            if (list.id === listId) {
                return {
                    ...list,
                    // Merge new state (columnWidths, sortConfig, columnOrder)
                    ...newState
                };
            }
            return list;
        });
        onDashboardChange({ lists: newLists });
    }, [lists, onDashboardChange]);

    const toggleTicketExpansion = useCallback((ticketId) => {
        setExpandedTickets(prev => {
            const newSet = new Set(prev);
            if (newSet.has(ticketId)) {
                newSet.delete(ticketId);
            } else {
                newSet.add(ticketId);
            }
            return newSet;
        });
    }, []);

    const ticketMapWithChildren = useMemo(() => {
        const map = new Map(optimisticTickets.map(t => [t._id, { ...t, children: [] }]));
        for (const ticket of optimisticTickets) {
            const parentId = typeof ticket.parentId === 'object' ? ticket.parentId?._id : ticket.parentId;
            
            if (parentId && map.has(parentId)) {
                map.get(parentId).children.push(ticket);
            }
        }
        return map;
    }, [optimisticTickets]);

    const displayTickets = useMemo(() => {
        const result = [];
        let topLevelTickets = optimisticTickets.filter(t => {
            const parentId = typeof t.parentId === 'object' ? t.parentId?._id : t.parentId;
            return !parentId || !ticketMapWithChildren.has(parentId);
        });

        topLevelTickets.sort((a, b) => {
            const aIsClosed = a.status === 'Closed';
            const bIsClosed = b.status === 'Closed';
            if (aIsClosed !== bIsClosed) {
                return aIsClosed ? 1 : -1;
            }
            return new Date(b.createdAt) - new Date(a.createdAt);
        });

        const isAdmin = user?.roles.includes('Admin') || user?.roles.includes('Giga Admin');
    
        for (const ticket of topLevelTickets) {
            result.push(ticket);
            if (expandedTickets.has(ticket._id)) {
                const populatedTicket = ticketMapWithChildren.get(ticket._id);
                if (populatedTicket && populatedTicket.children.length > 0) {
                    let visibleChildren = populatedTicket.children;
                    if (!isAdmin) {
                        if (currentUserTechnicianProfile) {
                            visibleChildren = populatedTicket.children.filter(child =>
                               (child.assignedTechnicianIds || []).includes(currentUserTechnicianProfile._id)
                            );
                        } else {
                            visibleChildren = [];
                        }
                    }
                    const sortedChildren = visibleChildren.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
                    for (const child of sortedChildren) {
                        result.push({ ...child, isSubTicket: true });
                    }
                }
            }
        }
        return result;
    }, [optimisticTickets, expandedTickets, ticketMapWithChildren, user, currentUserTechnicianProfile]);

    const handleSelectOneInList = useCallback((ticketId) => {
        const isCurrentlySelected = selectedIds.includes(ticketId);
        
        const getDescendantIds = (id) => {
            const ticket = ticketMapWithChildren.get(id);
            if (!ticket || !ticket.children || ticket.children.length === 0) return [];
            return ticket.children.flatMap(child => [child._id, ...getDescendantIds(child._id)]);
        };

        const descendants = getDescendantIds(ticketId);
        const idsToToggle = [ticketId, ...descendants];

        setSelectedIds(prevSelectedIds => {
            const newSelectedIds = new Set(prevSelectedIds);
            if (isCurrentlySelected) {
                idsToToggle.forEach(id => newSelectedIds.delete(id));
            } else {
                idsToToggle.forEach(id => newSelectedIds.add(id));
            }
            return Array.from(newSelectedIds);
        });
    }, [selectedIds, ticketMapWithChildren]);

    const handleSelectAllInList = useCallback((isChecked, paginatedIds) => {
        if (isChecked) {
            const allIdsToSelect = new Set();
            paginatedIds.forEach(id => {
                allIdsToSelect.add(id);
                const ticket = ticketMapWithChildren.get(id);
                if (ticket && ticket.children) {
                    ticket.children.forEach(child => allIdsToSelect.add(child._id));
                }
            });
            setSelectedIds(prev => Array.from(new Set([...prev, ...allIdsToSelect])));
        } else {
            const idsToRemove = new Set(paginatedIds);
            paginatedIds.forEach(id => {
                const ticket = ticketMapWithChildren.get(id);
                if (ticket && ticket.children) {
                    ticket.children.forEach(child => idsToRemove.add(child._id));
                }
            });
            setSelectedIds(prev => prev.filter(id => !idsToRemove.has(id)));
        }
    }, [ticketMapWithChildren]);

    const handleDeleteAndClearSelection = (ids) => {
        onDeleteMultipleTickets(ids);
        setSelectedIds([]);
    };

    const handleDragEnd = useCallback((result) => {
        const { destination, source, draggableId } = result;
        if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) { return; }
        setOptimisticTickets(prevTickets => {
            const ticketToMove = prevTickets.find(t => t._id === draggableId);
            if (!ticketToMove) return prevTickets;
            const destinationColumnTickets = prevTickets.filter(t => (t.ticketGroupId || 'unassigned') === destination.droppableId && t._id !== draggableId).sort((a, b) => (a.position || 0) - (b.position || 0));
            const prevTicket = destinationColumnTickets[destination.index - 1];
            const nextTicket = destinationColumnTickets[destination.index];
            let newPosition;
            if (!prevTicket && !nextTicket) newPosition = Date.now();
            else if (!prevTicket) newPosition = (nextTicket.position || 0) - 1000;
            else if (!nextTicket) newPosition = (prevTicket.position || 0) + 1000;
            else newPosition = ((prevTicket.position || 0) + (nextTicket.position || 0)) / 2;
            const newTicketGroupId = destination.droppableId === 'unassigned' ? null : destination.droppableId;
            onUpdateTicket(draggableId, { ticketGroupId: newTicketGroupId, position: newPosition });
            return prevTickets.map(ticket => ticket._id === draggableId ? { ...ticket, ticketGroupId: newTicketGroupId, position: newPosition } : ticket );
        });
    }, [onUpdateTicket]);

    const handleLayoutChange = (layout, newLayouts) => { if (!fullscreenId) { onDashboardChange({ layouts: newLayouts }); } };
    const onBreakpointChange = (breakpoint, cols) => { setCurrentBreakpoint(breakpoint); };
    
    // ✅ UPDATED: Save Logic to handle Edit Mode
    const saveListConfig = (config) => {
        if (editingListId) {
            // EDIT MODE: Update existing list
            const newLists = lists.map(list => {
                if (list.id === editingListId) {
                    return { ...list, ...config };
                }
                return list;
            });
            onDashboardChange({ lists: newLists });
            setEditingListId(null); // Reset edit state
        } else {
            // CREATE MODE: Add new list
            const newId = new Date().getTime().toString();
            const newList = { id: newId, ...config };
            const currentLayout = layouts[currentBreakpoint] || [];
            const newItemLayout = { i: newId, x: (currentLayout.length * 6) % 12, y: Infinity, w: 6, h: 10, minW: 4, minH: 6 }; 
            onDashboardChange({ lists: [...lists, newList], layouts: { ...layouts, [currentBreakpoint]: [...currentLayout, newItemLayout] } });
        }
    };

    const removeList = (idToRemove) => { if (fullscreenId === idToRemove) setFullscreenId(null); const newLists = lists.filter(list => list.id !== idToRemove); const newLayouts = { ...layouts }; Object.keys(newLayouts).forEach(bp => { if (Array.isArray(newLayouts[bp])) newLayouts[bp] = newLayouts[bp].filter(item => item.i !== idToRemove); }); onDashboardChange({ lists: newLists, layouts: newLayouts }); };
    const toggleFullscreen = (listId) => setFullscreenId(prevId => (prevId === listId ? null : listId));
    const handleAddSmartList = (config) => { onDashboardChange({ smartLists: [...smartLists, config] }); };
    
    const handleDeleteSmartList = (idToRemove) => {
        showConfirm(
            "Are you sure you want to delete this smart list?",
            () => {
                const newSmartLists = smartLists.filter(list => list.id !== idToRemove);
                onDashboardChange({ smartLists: newSmartLists });
            },
            'Delete Smart List',
            true
        );
    };

    // ✅ NEW: Handler to open modal in Edit Mode
    const handleEditList = (listId) => {
        setEditingListId(listId);
        setConfigModalOpen(true);
    };

    // ✅ NEW: Handler to close modal and reset edit state
    const handleCloseConfigModal = () => {
        setConfigModalOpen(false);
        setEditingListId(null);
    };

    if (isMobile) {
        return (
            <>
                 <AddTicketModal isOpen={isAddTicketModalOpen} onClose={() => setAddTicketModalOpen(false)} onCreateTicket={onCreateTicket} communities={communities} departments={departments} technicians={technicians} contacts={contacts} onAddContact={onAddContact} />
                 <MobileTicketsView 
                    tickets={optimisticTickets}
                    technicians={technicians}
                    appointments={appointments}
                    onNewTicketClick={() => setAddTicketModalOpen(true)}
                    canManageTickets={canManageTickets}
                    expandedTickets={expandedTickets}
                    toggleTicketExpansion={toggleTicketExpansion}
                    ticketMapWithChildren={ticketMapWithChildren}
                    myTechnicianId={currentUserTechnicianProfile ? currentUserTechnicianProfile._id : null}
                />
            </>
        )
    }

    return (
        <>
            <AddTicketModal isOpen={isAddTicketModalOpen} onClose={() => setAddTicketModalOpen(false)} onCreateTicket={onCreateTicket} communities={communities} departments={departments} technicians={technicians} contacts={contacts} onAddContact={onAddContact} />
            <ManageGroupsModal isOpen={isManageGroupsModalOpen} onClose={() => setIsManageGroupsModalOpen(false)} ticketGroups={ticketGroups} onAdd={onAddGroup} onDelete={onDeleteGroup} />
            
            {/* ✅ UPDATED: Pass initialConfig if editing, and use new handlers */}
            <NewListModal 
                isOpen={isConfigModalOpen} 
                onClose={handleCloseConfigModal} 
                onAddList={saveListConfig} 
                listType="Ticket" 
                columnOptions={ALL_TICKET_COLUMNS.map(c => ({ header: c.header, accessor: c.accessor }))} 
                filterGroups={listViewFilterGroups} 
                initialConfig={editingListId ? lists.find(l => l.id === editingListId) : null}
            />
            
            <NewSmartListModal isOpen={isSmartListModalOpen} onClose={() => setIsSmartListModalOpen(false)} onSave={handleAddSmartList} communities={communities} />
            
            <div className={`page-container ${fullscreenId ? 'dashboard-fullscreen-mode' : ''}`}>
                <div className="page-header">
                    <div><h2 className="page-title">Tickets Dashboard</h2><p className="page-subtitle">Add, arrange, and customize your ticket views.</p></div>
                    <div className="page-actions">
                        {viewMode === 'list' && (<button className="btn btn-secondary" onClick={() => setConfigModalOpen(true)}><FiPlus className="btn-icon"/> Add List</button>)}
                        {viewMode === 'group' && (<button className="btn btn-secondary" onClick={() => setIsSmartListModalOpen(true)}><FiFilter className="btn-icon"/> Add Smart Group</button>)}
                        {canManageTickets && (<>
                            {viewMode === 'group' && (<button className="btn btn-secondary" onClick={() => setIsManageGroupsModalOpen(true)}><FiLayers className="btn-icon" /> Manage Groups</button>)}
                            <button className="btn btn-primary" onClick={() => setAddTicketModalOpen(true)}><FiPlus className="btn-icon" /> New Ticket</button>
                        </>)}
                    </div>
                </div>

                <div className="grid-container" key={viewMode}> 
                    {viewMode === 'list' ? (
                        isLoading ? null : (
                            <ResponsiveGridLayout className="layout" layouts={layouts} onLayoutChange={handleLayoutChange} onBreakpointChange={onBreakpointChange} breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }} cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }} rowHeight={30} draggableHandle=".grid-card-header" draggableCancel=".grid-card-remove-btn, .grid-card-expand-btn" isDraggable={!fullscreenId} isResizable={!fullscreenId}>
                                {lists.map((list) => {
                                    const isFullscreen = fullscreenId === list.id;
                                    const displayTicketsForList = (() => {
                                        const matchingParentIds = new Set();
                                        return displayTickets.filter(ticket => {
                                            if (!ticket.isSubTicket) {
                                                const matches = applyTicketFilters(ticket, list.filters);
                                                if (matches) {
                                                    matchingParentIds.add(ticket._id);
                                                }
                                                return matches;
                                            } else {
                                                const pId = typeof ticket.parentId === 'object' ? ticket.parentId?._id : ticket.parentId;
                                                return matchingParentIds.has(pId);
                                            }
                                        });
                                    })();
                                    
                                    const getColumnRender = (accessor) => { 
                                        if (accessor === 'subject') {
                                            return (t) => {
                                                const hasChildren = ticketMapWithChildren.get(t._id)?.children.length > 0;
                                                const isExpanded = expandedTickets.has(t._id);
                                                const isCheckedInByMe = currentUserTechnicianProfile && t.activeCheckIns?.some(ci => ci.technicianId === currentUserTechnicianProfile._id);
                                                return (
                                                    <div className="subject-cell" style={{ display: 'flex', alignItems: 'center' }}>
                                                        {t.isSubTicket ? (
                                                            <span className="sub-ticket-indicator">└</span>
                                                        ) : hasChildren ? (
                                                            <button className="sub-ticket-toggle" onClick={(e) => { e.stopPropagation(); toggleTicketExpansion(t._id); }}>
                                                                {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
                                                            </button>
                                                        ) : (
                                                            <span className="sub-ticket-toggle-placeholder"></span>
                                                        )}
                                                        {isCheckedInByMe && <span className="active-timer-dot" title="You are clocked in"></span>}
                                                        <span className="subject-text">{t.subject}</span>
                                                    </div>
                                                );
                                            };
                                        }

                                        if (accessor === 'status') { 
                                            return (t) => { 
                                                const isOverdue = isOverdueChecker(t); 
                                                const isInProgress = t.status === 'In Progress';
                                                const subTicketActive = checkSubTicketActivity(t._id, optimisticTickets);
                                                let badgeClass = `status-${t.status.toLowerCase().replace(' ', '-')}`;
                                                if (isInProgress) badgeClass = 'status-ticket-in-progress';

                                                return (
                                                    <div className="status-with-timer" style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                                                        {isOverdue && <span className="tag priority-critical" style={{ marginRight: '0' }}>Overdue</span>}
                                                        {subTicketActive && !isInProgress && !t.isSubTicket && (
                                                            <span className="status-badge status-sub-ticket-active" title="A sub-ticket is currently in progress" style={{ fontSize: '0.75rem' }}>
                                                                Sub Active
                                                            </span>
                                                        )}
                                                        <span className={`status-badge ${badgeClass}`}>{t.status}</span>
                                                        {t.activeCheckIns && t.activeCheckIns.length > 0 && <TimeTrackingBadge startTime={t.activeCheckIns[0].checkInTime} />}
                                                    </div>
                                                ); 
                                            } 
                                        } 
                                        if (accessor === 'assignedTo') { return (ticket) => { if (!ticket.assignedTechnicianIds || ticket.assignedTechnicianIds.length === 0) return 'Unassigned'; if (!technicians) return 'Loading...'; const names = ticket.assignedTechnicianIds.map(id => { const tech = technicians.find(t => t._id === id); return tech ? tech.name : null; }).filter(Boolean); return names.length > 0 ? names.join(', ') : 'Unassigned'; }; } 
                                        return ALL_TICKET_COLUMNS.find(c => c.accessor === accessor)?.render; 
                                    };
                                    
                                    const columnsForThisList = list.columns
                                        .filter(accessor => accessor !== 'ticketGroupId')
                                        .filter(accessor => !isClient || accessor !== 'priority')
                                        .map(accessor => { 
                                            const baseCol = ALL_TICKET_COLUMNS.find(c => c.accessor === accessor); 
                                            if (!baseCol) { return null; } 
                                            return { ...baseCol, render: getColumnRender(accessor) || baseCol.render }; 
                                        })
                                        .filter(Boolean);

                                    // PREPARE SAVED STATE
                                    const savedState = {
                                        columnWidths: list.columnWidths || {},
                                        sortConfig: list.sortConfig || null,
                                        columnOrder: list.columnOrder || []
                                    };

                                    return (
                                        <div key={list.id} className={`grid-card ${isFullscreen ? 'is-fullscreen' : ''}`}>
                                            <div className="grid-card-header" onDoubleClick={() => toggleFullscreen(list.id)}>
                                                <h3 className="grid-card-title">{list.title}</h3>
                                                <div className="grid-card-actions">
                                                    {/* ✅ NEW: Edit Button */}
                                                    <button 
                                                        className="grid-card-expand-btn" 
                                                        onClick={() => handleEditList(list.id)} 
                                                        title="Edit List Settings"
                                                    >
                                                        <FiEdit2 />
                                                    </button>

                                                    <button className="grid-card-expand-btn" onClick={() => toggleFullscreen(list.id)} title={isFullscreen ? 'Restore' : 'Expand'}>{isFullscreen ? <FiMinimize /> : <FiMaximize />}</button>
                                                    <button className="grid-card-remove-btn" onClick={() => removeList(list.id)} title="Remove this list"><FiX /></button>
                                                </div>
                                            </div>
                                            <div className="grid-card-body">
                                                <ListView 
                                                    items={displayTicketsForList} 
                                                    columns={columnsForThisList} 
                                                    onItemClick={(id) => navigate(`/tickets/${id}`)} 
                                                    onDeleteItems={handleDeleteAndClearSelection} 
                                                    searchKeys={['client', 'subject', '_id']} 
                                                    searchPlaceholder="Search this list..." 
                                                    itemNoun="ticket"
                                                    rowClassName={(ticket) => { 
                                                        const classes = []; 
                                                        const isCheckedInByMe = currentUserTechnicianProfile && ticket.activeCheckIns?.some(ci => ci.technicianId === currentUserTechnicianProfile._id);
                                                        const isInProgress = ticket.status === 'In Progress';

                                                        if (ticket.isSubTicket) classes.push('sub-ticket-row table-animated-rows'); 
                                                        
                                                        if (isCheckedInByMe) {
                                                            classes.push('checked-in-by-me');
                                                        } else if (ticket.status === 'Closed') {
                                                            classes.push('closed-ticket-row');
                                                        } else if (isOverdueChecker(ticket)) {
                                                            classes.push('overdue-ticket-row'); 
                                                        } else if (isInProgress) {
                                                            classes.push('status-in-progress-row');
                                                        }
                                                        return classes.join(' '); 
                                                    }}
                                                    selectedIds={selectedIds}
                                                    onSelectOne={handleSelectOneInList}
                                                    onSelectAll={handleSelectAllInList}
                                                    enableSelection={canManageTickets} 
                                                    // PASS STATE PROPS
                                                    savedState={savedState}
                                                    onStateChange={(updates) => handleListStateChange(list.id, updates)}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </ResponsiveGridLayout>
                        )
                    ) : (
                        isLoading ? (<div className="loading-indicator">Loading Kanban Board...</div>) : (
                            <KanbanBoard 
                                tickets={optimisticTickets} 
                                onDragEnd={handleDragEnd} 
                                ticketGroups={ticketGroups} 
                                isOverdueChecker={isOverdueChecker} 
                                smartLists={smartLists} 
                                communities={communities} 
                                onDeleteSmartList={handleDeleteSmartList}
                                allTickets={optimisticTickets}
                            />
                        )
                    )}
                    {(viewMode === 'list' && lists.length === 0) && (<div className="empty-state-container"><div className="empty-state-content"><h3>Your dashboard is empty.</h3><p>Get started by creating a custom view of your tickets.</p><button className="btn btn-primary" onClick={() => setConfigModalOpen(true)}><FiPlus className="btn-icon" /> Add New List</button></div></div>)}
                </div>
            </div>
        </>
    );
};

export default Tickets;